// Recent-events reader. Phase 6A — surfaces the latest events for an
// entity so widgets can detect Path B (non-lifecycle) events that don't
// update state.current.yaml's last_event_id field.
//
// Why this exists: per packages/core/src/projector/validate-event.ts,
// non-lifecycle events skip state.current regeneration. So a widget
// reading state.lastEvent.eventType will keep seeing the prior
// lifecycle event indefinitely (e.g., kyc.verified) even after a Path B
// event lands (e.g., allocation.requested). This hook works around that
// by reading the timeline JSONL directly.
//
// Implementation: walks back DAYS_TO_WALK days from today, accumulates
// events from each day's _events.jsonl, returns up to `limit` newest-first.
// Stops walking once `limit` is reached.
//
// Used by: subscribe-allocate-form (Phase 6A) — to detect
// allocation.requested. Other Path B-detecting widgets in 6B/6C will
// reuse this pattern.

import { useEffect, useState } from 'react';
import { api } from '@rmint/shell';

export type RecentEvent = {
  eventId: string;
  eventType: string;
  occurredAt: string;
};

const DAYS_TO_WALK = 7;

function isoDateUtc(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function pastDates(daysBack: number): string[] {
  const out: string[] = [];
  const today = new Date();
  for (let i = 0; i < daysBack; i += 1) {
    const d = new Date(today);
    d.setUTCDate(d.getUTCDate() - i);
    out.push(isoDateUtc(d));
  }
  return out;
}

// Read one day's JSONL file. Returns events in file order (oldest first
// within the day) or null if 404. Throws on other 4xx/5xx so callers
// can surface real errors.
async function readDayEvents(
  entityRoot: string,
  date: string,
): Promise<RecentEvent[] | null> {
  const res = await api.get(
    `/dossier/${entityRoot}/timeline/${date}/_events.jsonl`,
    { responseType: 'text' },
  );
  if (res.status === 404) return null;
  if (res.status >= 400) {
    throw new Error(`recent-events read failed: ${res.status}`);
  }
  const text = typeof res.data === 'string' ? res.data : '';
  const lines = text.split('\n').filter((l) => l.trim().length > 0);
  return lines.map((line) => {
    const parsed = JSON.parse(line) as RecentEvent;
    return {
      eventId: parsed.eventId,
      eventType: parsed.eventType,
      occurredAt: parsed.occurredAt,
    };
  });
}

// Walk back DAYS_TO_WALK days; collect events newest-first; stop when
// we have `limit`. Returns [] if no events found in the window.
export async function fetchRecentEvents(
  entityRoot: string,
  limit: number,
): Promise<RecentEvent[]> {
  const collected: RecentEvent[] = [];
  for (const date of pastDates(DAYS_TO_WALK)) {
    const dayEvents = await readDayEvents(entityRoot, date);
    if (dayEvents === null) continue;
    // Within a day, file is oldest-first; reverse to newest-first.
    for (let i = dayEvents.length - 1; i >= 0; i -= 1) {
      collected.push(dayEvents[i]!);
      if (collected.length >= limit) return collected;
    }
  }
  return collected;
}

export type RecentEventsHook = {
  loading: boolean;
  events: RecentEvent[];
  error: Error | null;
};

// React hook. Optional refetchKey lets callers force a re-fetch (mirrors
// the useSubscriptionState pattern; bump after a POST to see the new
// event surface).
export function useRecentEvents(
  entityRoot: string | undefined,
  limit: number,
  refetchKey: number = 0,
): RecentEventsHook {
  const [loading, setLoading] = useState<boolean>(Boolean(entityRoot));
  const [events, setEvents] = useState<RecentEvent[]>([]);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!entityRoot) {
      setLoading(false);
      setEvents([]);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const result = await fetchRecentEvents(entityRoot, limit);
        if (!cancelled) {
          setEvents(result);
          setLoading(false);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e as Error);
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [entityRoot, limit, refetchKey]);

  return { loading, events, error };
}
